import { trpc } from '@tg/app-core';
import { plural3 } from '@tg/domain';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { D, MONTHS, addDays, dateLong, hhmm, iso } from '../lib/dates';
import { fmtN, pmName, useCatalog, useMe, useToday } from '../lib/data';
import { setSelDate } from '../lib/nav';
import { I } from '../ui/icons';
import { Tabs } from '../ui/parts';

/* E0. Лента событий клиента (CLI-7): по дням, у непрочитанного — точка.
   Открыл ленту — всё прочитано: точки уйдут при следующем входе. */
const EVT = {
  plan: { icon: 'cal', tag: 'Новая тренировка' },
  msg: { icon: 'msg', tag: 'Сообщение тренера' },
  reply: { icon: 'msg', tag: 'Ответ тренера' },
  pr: { icon: 'trophy', tag: 'Новый максимум' },
  chg: { icon: 'cal', tag: 'Тренировка изменена' },
  meas: { icon: 'scale', tag: 'Новый замер' },
} as const;

interface Ev {
  id: string;
  t: keyof typeof EVT;
  d: string;
  time: string;
  title: string;
  text: string;
  quote?: string;
  go: string;
  date?: string;
  isNew: boolean;
}

export function Feed() {
  const nav = useNavigate();
  const today = useToday();
  const me = useMe();
  const cat = useCatalog();
  const feed = trpc.clientApp.feed.useQuery(undefined, { staleTime: 0 });
  const utils = trpc.useUtils();
  const read = trpc.clientApp.readFeed.useMutation();
  /* Точки — по состоянию на момент открытия ленты */
  const unreadAtOpen = useRef<Set<string> | null>(null);
  if (!unreadAtOpen.current && feed.data && !feed.isPlaceholderData) unreadAtOpen.current = new Set(feed.data.events.filter((e) => !e.readAt).map((e) => e.id));

  useEffect(() => {
    if (!feed.data?.unread) return;
    read.mutate(undefined, {
      onSuccess: () => utils.clientApp.feed.setData(undefined, (f) => (f ? { ...f, unread: 0, events: f.events.map((e) => ({ ...e, readAt: e.readAt || new Date() })) } : f)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed.data?.unread]);

  /* Названия тренировок для «Новой тренировки» */
  const dates = (feed.data?.events || []).filter((e) => e.type === 'workout.new' || e.type === 'workout.changed').map((e) => String((e.payload as { date?: string }).date || ''));
  const from = dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : today;
  const to = dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : today;
  const cal = trpc.clientApp.calendar.useQuery({ from, to }, { enabled: dates.length > 0 });
  const titles = new Map((cal.data || []).map((d) => [d.date, d]));

  const tr = me.data?.trainer;
  const trName = tr ? [tr.first, tr.last].filter(Boolean).join(' ') : 'Тренер';
  const events = useMemo(() => {
    const out: Ev[] = [];
    for (const e of feed.data?.events || []) {
      const p = (e.payload || {}) as Record<string, unknown>;
      const at = new Date(e.createdAt as unknown as string);
      const base = { id: e.id, d: iso(at), time: hhmm(at), isNew: unreadAtOpen.current ? unreadAtOpen.current.has(e.id) : !e.readAt };
      const date = typeof p.date === 'string' ? p.date : undefined;
      if (e.type === 'workout.new' && date) {
        const c = titles.get(date);
        out.push({ ...base, t: 'plan', go: '/day/' + date, date, title: c?.title || 'Тренировка', text: 'Тренировка на ' + dateLong(date) + (c ? ' · ' + c.has + ' ' + plural3(c.has, 'блок', 'блока', 'блоков') : '') });
      } else if (e.type === 'workout.changed' && date) {
        const c = titles.get(date);
        out.push({ ...base, t: 'chg', go: '/day/' + date, date, title: c?.title || 'Тренировка', text: 'Тренер изменил тренировку на ' + dateLong(date) });
      } else if (e.type === 'measure') {
        const UN: [string, string, string][] = [
          ['weight', 'Вес', 'кг'],
          ['waist', 'Талия', 'см'],
          ['chest', 'Грудь', 'см'],
          ['hips', 'Бёдра', 'см'],
          ['fat', 'Жир', '%'],
        ];
        const vals = UN.filter(([k]) => p[k] != null).map(([k, n, u]) => n + ' ' + fmtN(p[k] as number, true) + (u === '%' ? '\u00a0%' : '\u00a0' + u));
        out.push({ ...base, t: 'meas', go: '/profile', title: vals.join(' · ') || 'Замер', text: 'Тренер внёс замер' });
      } else if (e.type === 'trainer.reply') {
        const day = p.scope === 'day';
        out.push({ ...base, t: day ? 'msg' : 'reply', go: date ? '/day/' + date : '/', date, title: trName, text: String(p.text || ''), quote: date ? 'к тренировке на ' + dateLong(date) : undefined });
      } else if (e.type === 'record') {
        const k = String(p.key || '');
        out.push({
          ...base,
          t: 'pr',
          go: '/profile',
          title: pmName(k, cat) + ' — ' + fmtN(p.kg as number) + ' кг',
          text: (p.by === 'trainer' ? 'Тренер обновил максимум' : 'Записан новый максимум') + (p.prev ? ', было ' + fmtN(p.prev as number) + ' кг' : '') + '. Рабочие веса в тренировках пересчитаны.',
        });
      }
    }
    return out;
  }, [feed.data, titles, trName, cat]);

  const label = (d: string) => (d === today ? 'Сегодня · ' : d === addDays(today, -1) ? 'Вчера · ' : '') + D(d).getDate() + ' ' + MONTHS[D(d).getMonth()];
  const groups: [string, Ev[]][] = [];
  for (const e of events) {
    const g = groups[groups.length - 1];
    if (g && g[0] === e.d) g[1].push(e);
    else groups.push([e.d, [e]]);
  }
  const open = (e: Ev) => {
    if (e.date) setSelDate(e.date);
    nav(e.go);
  };
  return (
    <>
      <header className="hhead">
        <div className="wmk">Лента</div>
      </header>
      <div className="pg feed">
        {groups.length ? (
          groups.map(([d, evs]) => (
            <Fragment key={d}>
              <div className="evday">{label(d)}</div>
              <div className="glist evs">
                {evs.map((e) => (
                  <button key={e.id} className={'ev' + (e.isNew ? ' new' : '')} onClick={() => open(e)}>
                    <span className={'evi t-' + e.t}>
                      <I n={EVT[e.t].icon} />
                    </span>
                    <span className="evc">
                      <span className="evh">
                        <s>{EVT[e.t].tag}</s>
                        <time>{e.time}</time>
                      </span>
                      <b>{e.title}</b>
                      <span className="evt">{e.text}</span>
                      {e.quote ? <em>{e.quote}</em> : null}
                    </span>
                  </button>
                ))}
              </div>
            </Fragment>
          ))
        ) : feed.data ? (
          <div className="card empty">
            <I n="bell" />
            <b>Тихо</b>
            <span>Новых событий нет</span>
          </div>
        ) : null}
      </div>
      <Tabs on="feed" />
    </>
  );
}
